import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { API_BASE } from '../api/client.js'

/**
 * Trustline platform admin screen at /admin (outside the tenant login).
 * Reached by one click from the Trustline Admin Hub (single sign-on stores the
 * token) or by signing in here with a platform admin email and password.
 * The token lives in sessionStorage only, so closing the tab signs you out.
 */
const KEY = 'tfleet_admin_token'
interface Tenant { id: string; company_name: string; tenant_code: string; subscription_status: string; gstin: string | null; created_at: string }

async function call<T>(path: string, init: RequestInit = {}, token?: string | null): Promise<T> {
  const res = await fetch(`${API_BASE}/api/admin${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) },
  })
  const body = await res.json().catch(() => null)
  if (!res.ok) throw Object.assign(new Error(body?.error?.message ?? `Request failed (${res.status})`), { status: res.status })
  return body as T
}

export function AdminPage() {
  const [token, setToken] = useState<string | null>(() => { try { return sessionStorage.getItem(KEY) } catch { return null } })
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<{ code: string; password: string } | null>(null)
  const [login, setLogin] = useState({ email: '', password: '' })
  const [form, setForm] = useState({ company_name: '', tenant_code: '', owner_name: '', owner_phone: '', default_language: 'en' })

  const signOut = useCallback(() => { try { sessionStorage.removeItem(KEY) } catch { /* ignore */ } setToken(null); setTenants([]) }, [])
  const load = useCallback(async () => {
    if (!token) return
    try { setTenants(await call<Tenant[]>('/tenants', {}, token)); setError(null) }
    catch (e) { if ((e as { status?: number }).status === 401) signOut(); else setError((e as Error).message) }
  }, [token, signOut])
  useEffect(() => { void load() }, [load])

  const doLogin = async (e: FormEvent) => {
    e.preventDefault(); setError(null)
    try {
      const r = await call<{ access_token: string }>('/login', { method: 'POST', body: JSON.stringify(login) })
      try { sessionStorage.setItem(KEY, r.access_token) } catch { /* ignore */ }
      setToken(r.access_token)
    } catch (err) { setError((err as Error).message) }
  }
  const onboard = async (e: FormEvent) => {
    e.preventDefault(); setError(null); setCreated(null)
    try {
      const r = await call<{ tenant_code: string; owner_temporary_password: string }>('/tenants', { method: 'POST', body: JSON.stringify(form) }, token)
      setCreated({ code: r.tenant_code, password: r.owner_temporary_password })
      setForm({ company_name: '', tenant_code: '', owner_name: '', owner_phone: '', default_language: 'en' })
      await load()
    } catch (err) { setError((err as Error).message) }
  }
  const setStatus = async (t: Tenant, action: 'suspend' | 'reactivate') => {
    if (action === 'suspend' && !window.confirm(`Suspend ${t.company_name}? Their users will be unable to sign in.`)) return
    try { await call(`/tenants/${t.id}/${action}`, { method: 'PATCH' }, token); await load() } catch (err) { setError((err as Error).message) }
  }

  return (
    <div className="tf-shell">
      <header className="tf-header">
        <span className="tf-logo">TFleet</span>
        <span className="tf-note">Platform admin — Trustline</span>
        <div className="tf-header-actions">{token && <button type="button" className="tf-button-link" onClick={signOut}>Sign out</button>}</div>
      </header>
      <main className="tf-main">
        {error && <p className="tf-error">{error}</p>}
        {!token ? (
          <form className="tf-card tf-form" onSubmit={doLogin} style={{ maxWidth: '24rem' }}>
            <h2>Platform admin sign-in</h2>
            <label className="tf-field">Email<input type="email" required value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} /></label>
            <label className="tf-field">Password<input type="password" required value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} /></label>
            <div className="tf-form-actions"><button className="tf-button-primary" type="submit">Sign in</button></div>
          </form>
        ) : (
          <>
            <form className="tf-card tf-form" onSubmit={onboard}>
              <h2>Onboard a company</h2>
              <div className="tf-grid-2">
                <label className="tf-field">Company name<input required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></label>
                <label className="tf-field">Company code (3–12 letters/digits, used at login)<input required value={form.tenant_code} onChange={(e) => setForm({ ...form, tenant_code: e.target.value })} /></label>
                <label className="tf-field">Owner name<input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} /></label>
                <label className="tf-field">Owner mobile (login)<input required value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} /></label>
                <label className="tf-field">Language
                  <select value={form.default_language} onChange={(e) => setForm({ ...form, default_language: e.target.value })}><option value="en">English</option><option value="ta">தமிழ்</option></select>
                </label>
              </div>
              {created && <p className="tf-override-banner">Company code <strong>{created.code}</strong>. Owner temporary password: <strong>{created.password}</strong>. Shown once — note it down and share it securely.</p>}
              <div className="tf-form-actions"><button className="tf-button-primary" type="submit">Create company</button></div>
            </form>

            <table className="tf-table" style={{ marginTop: '1.25rem' }}>
              <thead><tr><th>Company</th><th>Code</th><th>Status</th><th>GSTIN</th><th>Created</th><th /></tr></thead>
              <tbody>
                {tenants.length === 0 && <tr><td colSpan={6}>No companies yet.</td></tr>}
                {tenants.map((t) => (
                  <tr key={t.id}>
                    <td>{t.company_name}</td><td>{t.tenant_code}</td><td>{t.subscription_status}</td><td>{t.gstin ?? ''}</td>
                    <td>{new Date(t.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</td>
                    <td className="tf-row-actions">
                      {t.subscription_status === 'Suspended'
                        ? <button type="button" onClick={() => void setStatus(t, 'reactivate')}>Reactivate</button>
                        : <button type="button" onClick={() => void setStatus(t, 'suspend')}>Suspend</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </main>
    </div>
  )
}
